import React, { useEffect, useMemo, useState } from 'react'
import { clipboard, fetchApiData, socketPerso } from '../fnc.js'

const refConst = require('../constants.js').v

const storeOptions = [
	{ value: 'all', label: 'All stores' },
	{ value: '133845', label: 'Amazon EIO' },
	{ value: '133847', label: 'Amazon Gott' },
]

const fbaSuffixOptions = [
	{ value: 'all', label: 'All (_fba, _fba2, _fba3)' },
	{ value: 'fba', label: '_fba only' },
	{ value: 'fba2', label: '_fba2 only' },
	{ value: 'fba3', label: '_fba3 only' },
]

const fbaSuffixBadgeText = {
	all: 'FBA SKU: _fba, _fba2, and _fba3',
	fba: 'FBA SKU: _fba only',
	fba2: 'FBA SKU: _fba2 only',
	fba3: 'FBA SKU: _fba3 only',
}

function formatMoney(value) {
	if (value === null || value === undefined || value === '') return '-';
	const number = Number(value);
	if (Number.isNaN(number)) return '-';
	return `$${number.toFixed(2)}`;
}

function parseLookupAliases(lookupAliases) {
	if (lookupAliases === null || lookupAliases === undefined || lookupAliases === '') return [];
	return String(lookupAliases)
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean);
}

export default function FbaAmazonMinMaxReport(props) {
	if (!props.securityClearance(props)) return null;

	const [storeId, setStoreId] = useState('all');
	const [fbaSuffix, setFbaSuffix] = useState('all');
	const [rows, setRows] = useState([]);
	const [summary, setSummary] = useState({ totalRows: 0, missingMin: 0, missingMax: 0, storeBreakdown: {} });
	const [searchTerm, setSearchTerm] = useState('');
	const [refreshGuardrails, setRefreshGuardrails] = useState(true);
	const [activeListingsOnly, setActiveListingsOnly] = useState(true);
	const [lastRun, setLastRun] = useState(null);

	useEffect(() => {
		loadList();
	}, [storeId, fbaSuffix, activeListingsOnly]);

	const loadList = () => {
		props.loading(true);
		fetchApiData('tools', 'fba_amazon_minmax_report', {
			method: 'list',
			storeId,
			fbaSuffix,
			activeListingsOnly,
			limit: 2000,
		}, null, (response) => {
			props.loading(false);

			if (!response || !response.success) {
				setRows([]);
				setSummary({ totalRows: 0, missingMin: 0, missingMax: 0, storeBreakdown: {} });
				props.info({ error: response && response.error ? response.error : 'Unable to load the FBA Amazon list.' });
				return;
			}

			setRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
			setSummary(response.summary || { totalRows: 0, missingMin: 0, missingMax: 0, storeBreakdown: {} });
		});
	}

	const runRefresh = () => {
		const user = props.getUser && props.getUser() ? props.getUser() : null;
		const idAccount = user?.id;
		const uid = user?.uid || '';
		props.loading(true);
		socketPerso('task', {
			url: refConst.urlServer,
			internalReq: {
				api: 'tools',
				trigger: 'fba_amazon_minmax_report',
				method: 'run',
				storeId,
				fbaSuffix,
				refreshGuardrails,
				activeListingsOnly,
				generateReport: true,
				...(idAccount != null && idAccount !== '' ? { id_account: idAccount } : {}),
			},
			task: {
				op: 'insert',
				type: 'report',
				args: {
					uid,
					name: 'FBA Amazon Min/Max — refresh + report',
				},
			},
		}, (response) => {
			props.loading(false);

			if (!response || !response.success) {
				props.info({ error: response && response.error ? response.error : 'Unable to refresh the FBA Amazon report.' });
				return;
			}

			setRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
			setSummary(response.summary || { totalRows: 0, missingMin: 0, missingMax: 0, storeBreakdown: {} });
			setLastRun(response.data || null);
			const em = response.data?.reportEmail;
			let successMsg = 'FBA Amazon report refreshed.';
			if (em && !em.error && em.emailResult) {
				successMsg += ' The spreadsheet was emailed to your account address.';
			}
			props.info({ success: successMsg });
		});
	}

	const visibleRows = useMemo(() => {
		const term = searchTerm.trim().toLocaleLowerCase('en-US');
		if (!term) return rows;

		return rows.filter((row) => {
			return [row.sku, row.lookup_aliases, row.asin, row.amazon_title, row.store_name, row.internal_sku, row.internal_title]
				.filter((value) => value !== undefined && value !== null)
				.some((value) => String(value).toLocaleLowerCase('en-US').includes(term));
		});
	}, [rows, searchTerm]);

	const storeBreakdown = useMemo(() => {
		return Object.entries(summary.storeBreakdown || {});
	}, [summary]);

	const openLookupsModal = (row) => {
		const list = parseLookupAliases(row.lookup_aliases);
		props.modal({
			show: true,
			title: `Lookups — ${row.sku || ''}`,
			options: { width: 'modal-md' },
			html: () => (
				<div className="px-1">
					{list.length === 0 ? (
						<p className="text-muted mb-0">No lookup aliases for this product.</p>
					) : (
						<ul className="list-group list-group-flush">
							{list.map((alias) => (
								<li key={alias} className="list-group-item py-2 px-2 text-break">{alias}</li>
							))}
						</ul>
					)}
				</div>
			),
			return: { title: 'Close' },
		});
	};

	return <div className="container-fluid py-3">
		<div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
			<div>
				<h2 className="mb-1">FBA Amazon Min/Max Report</h2>
				<div className="text-muted">Build the ASIN list</div>
			</div>
			<div className="badge badge-dark p-2">v0.1.0</div>
		</div>

		<div className="card border-0 shadow-sm mb-4">
			<div className="card-body">
				<div className="alert alert-info mb-4 w-100 text-break" style={{ minWidth: 0, whiteSpace: 'normal' }} role="alert">
					Minimum and maximum prices shown here come from the last values stored for each listing. They may be out of date if they have not been refreshed recently. Use <strong>Refresh + Report</strong> below to pull current min/max from Amazon and regenerate the spreadsheet.
				</div>
				<div className="row align-items-end">
					<div className="col-xl-3 col-lg-4 mb-3">
						<label className="font-weight-bold">Store</label>
						<select className="form-control" value={storeId} onChange={(e) => setStoreId(e.target.value)}>
							{storeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
						</select>
					</div>
					<div className="col-xl-3 col-lg-4 mb-3">
						<label className="font-weight-bold">FBA SKU suffix</label>
						<select className="form-control" value={fbaSuffix} onChange={(e) => setFbaSuffix(e.target.value)}>
							{fbaSuffixOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
						</select>
					</div>
					<div className="col-xl-3 col-lg-4 mb-3">
						<label className="font-weight-bold">Search</label>
						<input
							type="text"
							className="form-control"
							placeholder="SKU, ASIN, title..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>
					<div className="col-xl-3 col-lg-4 mb-3">
						<div className="font-weight-bold mb-2">List &amp; refresh</div>
						<div className="custom-control custom-checkbox mb-2">
							<input className="custom-control-input" id="activeListingsOnly" type="checkbox" checked={activeListingsOnly} onChange={(e) => setActiveListingsOnly(e.target.checked)} />
							<label className="custom-control-label" htmlFor="activeListingsOnly">Active listings only</label>
						</div>
						<div className="custom-control custom-checkbox mb-2">
							<input className="custom-control-input" id="refreshGuardrails" type="checkbox" checked={refreshGuardrails} onChange={(e) => setRefreshGuardrails(e.target.checked)} />
							<label className="custom-control-label" htmlFor="refreshGuardrails">Refresh min/max from Amazon</label>
						</div>
					</div>
					<div className="col-xl-3 col-lg-12 mb-3 text-xl-right">
						<button type="button" className="btn btn-outline-secondary mr-2" onClick={loadList}>Load Current List</button>
						<button
							type="button"
							className="btn btn-primary"
							onClick={runRefresh}
							title="Runs on the server via the task queue; allow more than 4 minutes for guardrail refresh and report generation."
						>
							{`Refresh + Report (>4 min)`}
						</button>
					</div>
				</div>

				{lastRun?.report ? <div className="border-top pt-3 mt-2">
					<div className="row">
						<div className="col-lg-8 mb-2">
							<div className="small text-muted text-uppercase">Latest Report</div>
							<div className="font-weight-bold">{lastRun.report.path || 'Report created'}</div>
						</div>
						<div className="col-lg-4 text-lg-right">
							<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => clipboard(lastRun.report.path, props)}>Copy Report Path</button>
						</div>
					</div>
				</div> : null}
			</div>
		</div>

		<div className="row mb-4">
			<div className="col-lg-4 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<h5 className="mb-3">Store Breakdown</h5>
						{storeBreakdown.length === 0 ? <div className="text-muted">No rows loaded.</div> : storeBreakdown.map(([storeName, count]) => <div key={storeName} className="d-flex justify-content-between align-items-center border rounded px-3 py-2 mb-2">
							<div className="font-weight-bold">{storeName}</div>
							<span className="badge badge-primary badge-pill px-3 py-2">{count}</span>
						</div>)}
					</div>
				</div>
			</div>
			<div className="col-lg-8 mb-3">
				<div className="card border-0 shadow-sm h-100">
					<div className="card-body">
						<h5 className="mb-3">Last Refresh</h5>
						{!lastRun ? <div className="text-muted">No refresh has been run from this screen yet.</div> : <div>
							<div className="mb-2"><span className="font-weight-bold">Guardrail refresh:</span> {lastRun.guardrailRefresh ? `${lastRun.guardrailRefresh.data?.length || 0} store batch(es)` : 'Not run'}</div>
							<div><span className="font-weight-bold">Report:</span> {lastRun.report?.path || 'Not generated'}</div>
						</div>}
					</div>
				</div>
			</div>
		</div>

		<div className="card border-0 shadow-sm">
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center mb-3">
					<h5 className="mb-0">FBA Amazon Listing Report</h5>
					<span className="badge badge-light">
						{fbaSuffixBadgeText[fbaSuffix] || fbaSuffixBadgeText.fba}
						{activeListingsOnly ? ' · active listings' : ' · incl. inactive'}
					</span>
				</div>
				<div className="table-responsive">
					<table className="table table-hover align-middle mb-0">
						<thead className="thead-light">
							<tr>
								<th>Store</th>
								<th className="text-center">Lookups</th>
								<th>SKU</th>
								<th>ASIN</th>
								<th>Amazon Title</th>
								<th>Our Price</th>
								<th>Min</th>
								<th>Max</th>
							</tr>
						</thead>
						<tbody>
							{visibleRows.length === 0 ? <tr>
								<td colSpan="8" className="text-center text-muted py-5">No FBA Amazon listing matches the current filters.</td>
							</tr> : visibleRows.map((row) => <tr key={`${row.id_marketplace}-${row.id_listing}-${row.sku}`}>
								<td><span className="badge badge-info text-light px-3 py-2">{row.store_name}</span></td>
								<td className="text-center text-nowrap">
									<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openLookupsModal(row)}>Lookups</button>
								</td>
								<td className="font-weight-bold">{row.sku}</td>
								<td>{row.asin}</td>
								<td style={{ minWidth: '320px' }}>{row.amazon_title || <span className="text-muted">No title saved</span>}</td>
								<td>{formatMoney(row.our_price)}</td>
								<td className={row.minimum_price ? '' : 'text-danger'}>{formatMoney(row.minimum_price)}</td>
								<td className={row.maximum_price ? '' : 'text-danger'}>{formatMoney(row.maximum_price)}</td>
							</tr>)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
}